import { NGXLogger } from 'ngx-logger';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Drawer } from 'primeng/drawer';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { MegaMenuItem } from 'primeng/api'; //required when using MegaMenu
import {
  ActivatedRoute,
  GuardsCheckEnd,
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { LogoutService } from './services/logout.service';
import { reloadOnChunkLoadErrorOnce } from './helper/global-error-handler';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { PanelMenuModule } from 'primeng/panelmenu';
import { PanelModule } from 'primeng/panel';
import { SideBarMenuSharedService } from './services/side-bar-menu-shared.service';
import { catchError, debounceTime, filter, map, timeout } from 'rxjs/operators';
import { DashboardSocketService } from 'src/app/modules/dashboard-v6/services/dashboard-socket.service';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { FontSizeServiceService } from './services/font-size-service.service';
import { ToastModule } from 'primeng/toast';
import { SharedSubscriptionNotificationService } from './services/shared-subscription-notification.service';
import { SubscriptionStatus } from './components/extras/subscription-status.enum';
import { AuthService } from './services/auth.service';
import { fadeAnimation } from './commons/fadeAnimation';
import { RouterOutlet } from '@angular/router';
import {
  fadeInAnimation,
  loaderFadeInOut,
  rotateAnimation,
  scaleUpAnimation,
  slideInAnimation,
} from './commons/route-animations';
import { SideBarComponent } from './modules/layout/side-bar/side-bar.component';
import { UtilService } from './services/util.service';
import { ToastMessagesService } from './commons/services/toast-messages.service';
import {
  AppConfig,
  AppConfigService,
} from './modules/layout/app-config.service';
import { AppConstants } from './commons/app-constants';
import { Theme } from './commons/theme-constants';
import { ThemeService } from './services/theme.service';
import { ChartThemeService } from './commons/chart-theme.service';
import { SHARED_MODULES } from './modules/shared-imports';
import { HeaderComponent } from './modules/layout/header/header.component';
import { BrowserModule } from '@angular/platform-browser';
import { FooterComponent } from './modules/layout/footer/footer.component';
import { ScreenService } from './services/screen.service';
import { SessionExpiredDialogService } from './services/session-expired-dialog.service';
import { ForcedLogoutDialogService, ForcedLogoutDialogRequest } from './services/forced-logout-dialog.service';
import { clearLocalStorageKeepingDeviceIdentity } from './commons/utils/session-reset';
import { resetOfflineIdentityVerified } from './offline/offline-db';
import { AccessDeniedDialogService } from './services/access-denied-dialog.service';
import { PlanLimitDialogService } from './services/plan-limit-dialog.service';
import { TicketService } from './modules/support-tickets/services/ticket.service';
import { BetaService } from './services/beta/beta.service';
import { QualityService } from './services/quality/quality.service';
import { InventoryModuleService } from './services/inventory-module/inventory-module.service';
import { ServerErrorComponent } from './modules/extras/server-error/server-error.component';
import { ActivityTrackerService } from './services/analytics/activity-tracker.service';
import { environment } from 'src/environments/environment';
import { AiChatWidgetComponent } from './modules/layout/ai-chat-widget/ai-chat-widget.component';
import { AiChatWidgetSharedService } from './services/ai-chat-widget-shared.service';
import { ShellLayoutService } from './services/shell-layout.service';
import { OutdatedDoc, LegalAcceptanceStatus, LegalService, SIGNUP_AGREED_KEY } from './services/legal.service';
import { NetworkStatusService } from './services/network-status.service';
import { HttpActivityService } from './services/http-activity.service';
import { BootstrapService, BootstrapResponse } from './services/bootstrap.service';
import { OfflineLeaseService } from './services/offline-lease.service';
import { SyncEngineService } from './offline/sync-engine.service';
import { OfflinePrefetchService } from './offline/offline-prefetch.service';
import { StatusBarService } from './services/status-bar.service';
import { AppSplashService } from './services/app-splash.service';
import { MobileOAuthCallbackService } from './services/mobile-oauth-callback.service';
import { DeepLinkService } from './services/deep-link.service';
import { AndroidPaymentReturnService } from './services/android-payment-return.service';
import { NotificationSocketService } from './notifications/notification-socket.service';
import { SalesRefService } from './modules/crm/sales-ref.service';
import { PushNotificationService } from './services/push-notification.service';
import { WaApprovalComponent } from './modules/crm/wa-approval/wa-approval.component';
import { Capacitor } from '@capacitor/core';
import { isIosNative } from './commons/utils/platform';
import { hasWebSession } from './commons/utils/web-session';
import { scheduleOrphanedOverlayMaskSweep } from './commons/utils/overlay-mask-sweep';
import { SlowConnectionIndicatorComponent } from './commons/components/slow-connection-indicator/slow-connection-indicator.component';
import { PullToRefreshComponent } from './commons/pull-to-refresh/pull-to-refresh.component';
import { OpenInAppBannerComponent } from './commons/components/open-in-app-banner/open-in-app-banner.component';
import { EnableNotificationsPromptComponent } from './modules/tally-sheet/account-setup/enable-notifications-prompt/enable-notifications-prompt.component';
import { FeedbackHostComponent } from './commons/components/feedback-host/feedback-host.component';
import { SharedFilesSheetComponent } from './commons/components/shared-files-sheet/shared-files-sheet.component';
import { initOverflowChecker } from './commons/overflow-checker';
import { LlLoaderComponent } from './commons/components/ll-loader/ll-loader.component';
import { ScreenSupportPromptComponent } from './modules/screen-support/screen-support-prompt/screen-support-prompt.component';

(window as any).global = window;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    providers: [MessageService, ToastMessagesService],
    animations: [fadeInAnimation, loaderFadeInOut],
    imports: [SHARED_MODULES, HeaderComponent, HttpClientModule,
        SideBarComponent, FooterComponent,
        ServerErrorComponent, AiChatWidgetComponent, SlowConnectionIndicatorComponent, PullToRefreshComponent,
        OpenInAppBannerComponent, EnableNotificationsPromptComponent, FeedbackHostComponent, SharedFilesSheetComponent,
        WaApprovalComponent, LlLoaderComponent, ScreenSupportPromptComponent,
    ]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly logger = inject(NGXLogger);

  @Input() minimal: boolean = false;
  get isOffline(): boolean {
    return !this.networkStatusService.isOnline;
  }

  scales: number[] = [12, 13, 14, 15, 16];

  loading: boolean = false;
  // Ambient "some API call is in flight somewhere" signal, separate from `loading` above (which
  // is reserved for actual route navigation - lazy-chunk downloads + guard resolution). Drives a
  // thin, non-blocking top-of-viewport bar; see HttpActivityService for why it's debounced and
  // deliberately kept out of the route-loading path entirely.
  httpActivityVisible$!: Observable<boolean>;
  @ViewChild(SideBarComponent) sidebarComponent!: SideBarComponent;
  @ViewChild(AiChatWidgetComponent) aiChatWidget!: AiChatWidgetComponent;
  showErrorDialog: boolean = false;
  errorMessage!: string | null;
  showSessionExpiredDialog = false;
  sessionExpiredMessage = '';
  private sessionExpiredAccept: (() => void) | null = null;
  private sessionExpiredReject: (() => void) | null = null;
  showForcedLogoutDialog = false;
  forcedLogoutInfo: ForcedLogoutDialogRequest | null = null;
  showAccessDeniedDialog = false;
  accessDeniedMessage = '';

  showPlanLimitDialog = false;
  planLimitLabel = '';
  planLimitMessage = '';
  planLimitUsed: number | null = null;
  planLimitLimit: number | null = null;
  planLimitManageRoute: string | null = null;
  private lastSubscriptionToastKey: string | null = null;
  showCookieConsent = false;
  showCookiePreferences = false;
  analyticsCookiesEnabled = false;
  readonly isNativeApp = Capacitor.isNativePlatform();
  private readonly cookieConsentKey = "ll_cookie_consent";
  showLegalUpdateBanner = false;
  legalOutdatedDocs: OutdatedDoc[] = [];
  private legalCheckDone = false;
  private legalChangedSub: { unsubscribe(): void } | null = null;
  // Real fix 2026-08-27: which COOKIE policy version a save should be recorded against for a
  // logged-in user — overwritten by resolveCookieConsentForAccount() once bootstrap resolves;
  // this fallback only matters for the brief window before that first bootstrap response, and
  // matches the DB seed's own starting value (see prompt32-legal-acceptance.sql).
  private cookieConsentVersion = '1.0';
  private readonly legalBannerDismissKey = 'll_legal_banner_dismissed';
  // Set by <app-open-in-app-banner> via (visibleChange) — the banner is position:fixed, so the
  // page container needs top padding while it is on screen.
  appBannerVisible = false;

  // The auto-hiding header was removed 2026-09-12 — see the comment on .header in
  // app.component.css for why (it depended on scroll direction, which the Capacitor WebView does
  // not report reliably under contentInset:'automatic', so the header hid in the native app and
  // never came back). The header is now permanently pinned and needs no scroll state at all.

  // App-wide "Enable notifications?" priming dialog. Onboarding's own end-of-setup screens show
  // this at the right moment for new users; this path catches everyone else (existing users, and
  // anyone who onboarded before that prompt existed) on their next login — once ever per device,
  // gated on ll_push_prompt_seen. Native OS/browser permission cannot be defaulted on; a one-time
  // in-app prompt is the closest to "on by default" the platforms allow.
  showEnableNotificationsPrompt = false;
  private pushPromptEvaluated = false;
  private readonly analyticsScriptId = "ll-google-analytics";
  private readonly analyticsId = environment.gaTrackingId || '';
  // Only the FIRST NavigationEnd decides how the splash gets hidden — either immediately
  // (landing route isn't the dashboard, so there's no dashboard data to wait for) or left
  // alone for DashboardV4Component.load() to hide once its data actually arrives. Every
  // NavigationEnd after that is a normal in-app navigation and must never touch the splash.
  private isFirstNavigation = true;
  // Tracks whether the PREVIOUS NavigationEnd landed on a hidden (pre-auth: login/error/public)
  // route. Used to detect the exact login transition (hidden -> visible) so the push token can be
  // force-reregistered right then, rather than trusting PushNotificationService's own "did I
  // already send this" in-memory guard, which can't know the server-side row was deactivated by a
  // different path since the token was last sent. See PushNotificationService.forceReregisterOnLogin().
  private wasOnHiddenRoute = true;
  /** The address (path, query and #fragment) of the previous finished navigation — to tell a tab/anchor change from a real page change. */
  private lastNavUrl = '';

  isMenuExpand = false;
  //  items!: MenuItem[];
  items!: MegaMenuItem[];
  // showHeader = true;
  showFooter = true;
  showMenu = false;
  openTicketCount = 0;
  speedDialItems: MenuItem[] = [];
  aiChatEnabled = true;
  /** 2026-09-28 (live updates step 4): the ticket badge listens for TICKETS pushes — was a 60 s timer. */
  private ticketLiveSub: { unsubscribe(): void } | null = null;
  private readonly ticketLiveSocket = inject(DashboardSocketService);
  private readonly aiChatShared = inject(AiChatWidgetSharedService);
  /** The app-frame layout (per-person switch). Created here so it is on from the first screen. */
  private readonly shellLayout = inject(ShellLayoutService);
  isDashboardRoute = false;
  isTallySheetRoute = false;
  // showLogo = true;
  showLogoSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  ); // initial value
  showLogo$: Observable<boolean> = this.showLogoSubject.asObservable();

  private showHeaderSubject = new BehaviorSubject<boolean>(true);
  showHeader$ = this.showHeaderSubject.asObservable();

  showSideBar = false;
  showFontSize = false;
  showMenuButton = false;
  showDayNightScheme = false;
  showUserProfileButton = false;

  currentFontSize!: number;
  private fontSizeSubscription!: Subscription;
  private sessionExpiredSubscription!: Subscription;
  private forcedLogoutSubscription!: Subscription;
  private accessDeniedSubscription!: Subscription;
  private planLimitSubscription!: Subscription;
  private foregroundPushSubscription!: Subscription;
  constructor(
    private primengConfig: PrimeNG,
    private router: Router,
    private fontSizeService: FontSizeServiceService,
    private messageService: MessageService,
    private authService: AuthService,
    private utilService: UtilService,
    private appConfig: AppConfigService,
    private cdr: ChangeDetectorRef,
    private themeService: ThemeService,
    public  screenService: ScreenService,
    private sessionExpiredDialogService: SessionExpiredDialogService,
    private forcedLogoutDialogService: ForcedLogoutDialogService,
    private accessDeniedDialogService: AccessDeniedDialogService,
    private planLimitDialogService: PlanLimitDialogService,
    private ticketService: TicketService,
    public betaService: BetaService,
    public qualityService: QualityService,
    public inventoryModuleService: InventoryModuleService,
    private activityTrackerService: ActivityTrackerService,
    private http: HttpClient,
    private networkStatusService: NetworkStatusService,
    private httpActivityService: HttpActivityService,
    private bootstrapService: BootstrapService,
    private offlineLeaseService: OfflineLeaseService,
    // Constructor injection here is what makes this providedIn:'root' singleton actually get
    // instantiated at app startup — its own constructor sets up the online-transition effect
    // that drives offline sync. Never injected elsewhere, so without this line it would never
    // be created and queued offline writes would never replay.
    public syncEngineService: SyncEngineService,
    // public (not private) — the template binds directly to offlinePrefetchService.isRunning()
    // to show the "Preparing offline data…" progress bar.
    public offlinePrefetchService: OfflinePrefetchService,
    // Same eager-instantiation reason — sets the native status bar color/style at startup and
    // on every theme change. Never injected elsewhere.
    private statusBarService: StatusBarService,
    private appSplashService: AppSplashService,
    private mobileOAuthCallbackService: MobileOAuthCallbackService,
    private deepLinkService: DeepLinkService,
    private pushNotificationService: PushNotificationService,
    private androidPaymentReturnService: AndroidPaymentReturnService,
    private notificationSocketService: NotificationSocketService,
    private legalService: LegalService,
    private salesRefService: SalesRefService,
    // Eager-instantiated like the services above: sets the shared Chart.js look (see chart-theme.service.ts). Never injected elsewhere.
    private chartThemeService: ChartThemeService,
  ) {
    this.httpActivityVisible$ = this.httpActivityService.visible$;
    this.mobileOAuthCallbackService.init();
    this.deepLinkService.init();
    this.pushNotificationService.init();
    this.androidPaymentReturnService.init();
    this.updateBodyPadding();
    window.addEventListener('resize', this.updateBodyPadding.bind(this));
    window.addEventListener('resize', this.onWindowResize.bind(this));
    this.isDarkThemeEnabled = this.selectedTheme === Theme.Dark;

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof RouteConfigLoadStart ||
            event instanceof GuardsCheckStart ||
            event instanceof GuardsCheckEnd ||
            event instanceof NavigationEnd ||
            event instanceof NavigationError ||
            event instanceof NavigationCancel ||
            event instanceof NavigationSkipped
        )
      )
      .subscribe((event) => {
        if (event instanceof RouteConfigLoadStart) {
          // Covers the lazy-chunk-download window specifically — fires only when a route's
          // module actually needs fetching (never for already-loaded/eager routes).
          this.loading = true;
        } else if (event instanceof GuardsCheckStart) {
          // Real gap found 2026-09-10: when the DESTINATION route needs a lazy chunk AND the
          // CURRENT route has an in-flight canDeactivate confirm dialog (e.g. leaving a dirty
          // New Tallysheet form), RouteConfigLoadStart above had already set loading=true, and
          // nothing turned it back off until GuardsCheckEnd -- which only fires AFTER the user
          // has already answered the dialog. So the full-screen loader (z-index 9999, pointer-
          // events:all) sat on top of/alongside the "Unsaved Changes" dialog for the entire time
          // it was waiting on the user, looking stuck. GuardsCheckStart fires right as
          // canDeactivate/canActivate begin, before any confirm dialog can open, so hiding the
          // loader here closes that window -- GuardsCheckEnd below still owns the post-guards
          // state exactly as before.
          this.loading = false;
        } else if (event instanceof GuardsCheckEnd) {
          // Deferred from NavigationStart so the loader can't bury an in-flight canDeactivate confirm dialog.
          this.loading = event.shouldActivate;
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationError ||
          event instanceof NavigationCancel ||
          event instanceof NavigationSkipped
        ) {
          this.loading = false;
        }
        if (event instanceof NavigationError) {
          // The Router can swallow a lazy route's chunk-load failure entirely internally,
          // emitting it ONLY as this NavigationError — never as an uncaught exception, so
          // GlobalErrorHandler.handleError() is never called for it. Same reload-once guard,
          // shared with GlobalErrorHandler so whichever path fires first wins.
          if (reloadOnChunkLoadErrorOnce(event.error)) return;
        }
        if (event instanceof NavigationEnd) {
          scheduleOrphanedOverlayMaskSweep();
          // console.log('Navigation ended!');
          let currentUrl = event.urlAfterRedirects || event.url;
          // The "Session Expired" dialog can open while the user is still on an app page, and the
          // app itself then moves them to /login (forced logout / guard). Once the login page is
          // showing, the dialog has nothing left to say and only covers the sign-in form.
          if (this.showSessionExpiredDialog && currentUrl.startsWith('/login')) {
            this.showSessionExpiredDialog = false;
            this.sessionExpiredReject?.();
          }
          this.isDashboardRoute = currentUrl.startsWith('/dashboard-new');
          this.isTallySheetRoute = currentUrl.startsWith('/new-tallysheet');
          this.persistLastActiveRoute(currentUrl);
          if (currentUrl.includes('public/') || currentUrl.startsWith('/s/')) {
            const loggedIn = this.isLoggedInViaAuthCookie();
            // The video call guest page needs the whole height, so no footer there
            this.showFooter = !currentUrl.includes('public/live/');
            this.showHeaderSubject.next(true);
            this.showLogoSubject.next(true);
            this.showFontSize = true;
            this.showMenu = loggedIn;
            this.showMenuButton = loggedIn;
            this.showDayNightScheme = loggedIn;
            this.showUserProfileButton = loggedIn;
          } else if (currentUrl.includes('login')) {
            this.showFooter = false;
            this.showMenu = false;
            // this.showHeader = false;
            this.showHeaderSubject.next(false);

            // this.showLogo = false;
            this.showLogoSubject.next(false);

            this.showFontSize = false;
            this.showMenuButton = false;
            this.showDayNightScheme = false;
            this.showUserProfileButton = false;
            document.body.style.padding = '0';
          } else {
            // console.log("The URL does not contain the word 'public/'");
            // if (this.showMenu) {
            this.showFooter = true;
            // this.showHeader = true;
            this.showHeaderSubject.next(true);

            this.showMenu = true;
            this.showLogoSubject.next(true);

            this.showFontSize = true;
            this.showMenuButton = true;
            this.showDayNightScheme = true;
            this.showUserProfileButton = true;
          }
        }
      });
    this.utilService.showErrorDialog$.subscribe((show) => {
      this.showErrorDialog = show;
      // console.log('Dialog Status:', this.showErrorDialog);
    });
    this.utilService.errorMessage$.subscribe((code) => {
      this.errorMessage = code;
      // console.log('Error Message:', this.errorMessage);
    });

    this.sessionExpiredSubscription = this.sessionExpiredDialogService.dialogRequest$.subscribe((request) => {
      this.logger.debug('AppComponent :: session expired dialog request received', request);
      // Already on the login page: the user is signed out and looking at the sign-in form, so
      // this dialog is noise. Release the interceptor's "dialog pending" flag and stay quiet.
      if (this.router.url.startsWith('/login')) {
        request.onReject?.();
        return;
      }
      this.sessionExpiredMessage = request.message;
      this.sessionExpiredAccept = request.onAccept;
      this.sessionExpiredReject = request.onReject;
      this.showSessionExpiredDialog = true;
      //this.cdr.detectChanges();
    });

    this.forcedLogoutSubscription = this.forcedLogoutDialogService.dialogRequest$.subscribe((request) => {
      this.logger.debug('AppComponent :: forced logout dialog request received', request);
      // Same "already visible, don't stack another one" guard the session-expired dialog relies
      // on implicitly (SessionExpiredDialogService.sessionExpiredDialogVisible) — a dead session
      // can 401 on several concurrent requests at once.
      if (this.showForcedLogoutDialog) return;
      // Both dialogs are p-dialog at baseZIndex 25000, so whichever opens LAST covers the other.
      // The generic one can legitimately win that race (a sibling 401 with no kick marker takes
      // an extra /auth/refresh round-trip, so it usually arrives second — but not always). This
      // dialog knows WHY the user was signed out and by which device/location, so it always wins:
      // drop the vaguer one if it is already up. The interceptor suppresses any later ones via
      // ForcedLogoutDialogService.isActive().
      this.showSessionExpiredDialog = false;
      this.forcedLogoutInfo = request;
      this.showForcedLogoutDialog = true;
    });

    this.accessDeniedSubscription = this.accessDeniedDialogService.dialogRequest$.subscribe((message) => {
      this.logger.warn('AppComponent :: access denied dialog request received', { message });
      this.accessDeniedMessage = message;
      this.showAccessDeniedDialog = true;
    });

    this.planLimitSubscription = this.planLimitDialogService.dialogRequest$.subscribe((request) => {
      this.logger.warn('AppComponent :: plan limit dialog request received', request);
      const info = this.planLimitDialogService.describe(request.featureKey);
      this.planLimitLabel = info.label;
      this.planLimitManageRoute = info.manageRoute;
      this.planLimitMessage = request.message;
      this.planLimitUsed = request.used ?? null;
      this.planLimitLimit = request.limit ?? null;
      this.showPlanLimitDialog = true;
    });

    // Push arrived while the app is in the foreground (native or web) — show an in-app toast
    // instead of the OS-level banner background/closed pushes already get. Deliberately routed
    // through this Subject rather than PushNotificationService calling MessageService directly:
    // MessageService is re-provided at THIS component's level (see `providers` below), so only
    // code running through this component's own injector — like this subscription — resolves the
    // same instance the <p-toast key="push-notification-toast"> outlet below is listening to.
    this.foregroundPushSubscription = this.pushNotificationService.foregroundPush.subscribe((event) => {
      this.messageService.add({
        key: 'push-notification-toast',
        severity: 'info',
        summary: event.title,
        detail: event.body,
        life: 6000,
        data: event.data,
      });
    });
  }

  // Tapping the foreground push toast (see push-notification-toast outlet + subscription above).
  // v1 always lands on the notifications list, regardless of notificationType — the push payload
  // doesn't carry a resolvable deep-link route today, and building full per-type routing is
  // separate scope. Good enough to satisfy "tapping it goes somewhere useful."
  onPushToastClick(): void {
    this.router.navigate(['/notifications']);
  }

  ngAfterViewInit(): void {
    const currentUrl = this.router.url;
    this.updateFontSizeFromStorage();
    this.updateThemeFromStorage();
    this.buildSpeedDialItems();
  }

  ngOnInit(): void {
    // App bootstrapped successfully — clear the ChunkLoadError reload guard (see
    // GlobalErrorHandler) so a tab kept open across a future deploy can still auto-reload once.
    sessionStorage.removeItem('tms-chunk-reload-attempted');

    // An acceptance was recorded (account setup, sign-up, the dialog): re-read what is still outstanding, so the
    // "legal documents" banner and dialog do not stay on screen for someone who has just accepted.
    this.legalChangedSub = this.legalService.acceptancesChanged$.subscribe(() => this.refreshOutdatedLegalDocs());

    // TEMPORARY (2026-09-27): opt-in ?overflowcheck=1 finder for elements that make the page
    // wider than the phone. Does nothing unless switched on for this device.
    initOverflowChecker();

    // Sales CRM (Phase 3) — grab any ?ref=<salesperson> the visitor arrived with, and try to
    // hand a pending one to the backend (no-op unless logged in + the CRM is on + the company
    // has no ref yet). Best-effort, isolated — never throws.
    this.salesRefService.capture();
    this.salesRefService.flush();

    // call resize event

    this.utilService.onResize.subscribe((dimensions) => {
      this.logger.debug(`Width: ${dimensions.width}, Height: ${dimensions.height}`);
    });

    this.fontSizeSubscription = this.fontSizeService.currentFontSize.subscribe(
      (fontSize) => {
        this.currentFontSize = fontSize;
        document.documentElement.style.fontSize = fontSize + 'rem';
      }
    );
    this.initCookieConsent();


    this.primengConfig.ripple.set(true);
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        // Only the #fragment changed (a tab or an anchor on the same page): the page is the same, so the start-up work below
        // (bootstrap, sync, push token, polling) must not run again. A same-URL reload (pull to refresh) keeps its fragment, so it still runs.
        const navUrl: string = event.urlAfterRedirects || event.url || '';
        const fragmentOnly = this.lastNavUrl !== '' && navUrl !== this.lastNavUrl
          && navUrl.split('#')[0] === this.lastNavUrl.split('#')[0];
        this.lastNavUrl = navUrl;
        this.isDashboardRoute = event.urlAfterRedirects?.startsWith('/dashboard-new') ?? false;
        this.isTallySheetRoute = event.urlAfterRedirects?.startsWith('/new-tallysheet') ?? false;
        if (this.isFirstNavigation) {
          this.isFirstNavigation = false;
          // Landing on the dashboard: leave the splash up for DashboardV4Component.load()
          // to hide once real data has rendered. Landing anywhere else (login, a deep link,
          // etc.): there is no dashboard data coming, so hide right away — the 8s safety-net
          // timeout in AppSplashService would otherwise be the only thing to clear it.
          if (!this.isDashboardRoute) {
            this.appSplashService.hide();
          }
        }
        // Add the route paths for which you want to hide the components
        const hiddenRoutes = [
          'login',
          'forbidden',
          'hidden-route-3',
          'error',
          'test',
          'public',
        ];

        // Share-viewing routes: the /s/** dispatcher plus where it resolves to (tally-sheet/share,
        // legacy tallysheet-view). Chrome-free for an anonymous visitor — that's the whole point of
        // a share link — but a user who's already logged in and lands here via an in-app deep link
        // (Android/iOS) must not get stranded with no header/sidebar at all.
        const shareViewRoutes = ['tally-sheet/share', 'tallysheet-view'];
        const isShareRoute = event.urlAfterRedirects.startsWith('/s/') || event.urlAfterRedirects === '/s' ||
          shareViewRoutes.some((route) => event.urlAfterRedirects.startsWith(`/${route}`));
        const loggedIn = this.isLoggedInViaAuthCookie();

        const shouldHide = (isShareRoute && !loggedIn) || hiddenRoutes.some((route) =>
          event.urlAfterRedirects.startsWith(`/${route}`)
        );

        // this.showHeader = !shouldHide;
        this.showHeaderSubject.next(!shouldHide);

        // this.showFooter = !shouldHide;
        this.showMenu = !shouldHide;
        if (!shouldHide) {
          if (this.wasOnHiddenRoute) {
            // Transitioning from a hidden (pre-auth) route into the real app — this IS the login
            // moment. Force a fresh push-token registration here rather than letting
            // loadBootstrapData()'s own opportunistic call possibly skip it as "unchanged".
            this.pushNotificationService.forceReregisterOnLogin();
          }
          this.wasOnHiddenRoute = false;
          if (fragmentOnly) { return; }
          this.activityTrackerService.start();
          this.loadBootstrapData();
          this.maybePromptForPushPermission(event.urlAfterRedirects);
          this.startTicketBadgePolling();
          // Idempotent (guarded internally) — safe to call on every non-hidden route transition.
          // Gated here rather than in the constructor (like deepLinkService.init() etc.) because,
          // unlike those, this immediately attempts an authenticated WS handshake — calling it
          // before login would just spin on failed 401 handshakes every reconnectDelay.
          // Confirmed live 2026-08-27: a user still on the onboarding wizard (account-setup /
          // email-verification) has no company yet, so this connection has nothing meaningful to
          // deliver — skip starting it here; the next real route transition (post-onboarding)
          // calls init() again since it's idempotent-but-not-yet-started at that point.
          const onOnboardingRoute = event.urlAfterRedirects.startsWith('/account-setup')
              || event.urlAfterRedirects.startsWith('/email-verification');
          if (!onOnboardingRoute) {
            this.notificationSocketService.init();
          }
        } else {
          this.wasOnHiddenRoute = true;
          this.stopTicketBadgePolling();
        }
      });

    /* this.authService.isUserLoggedIn().subscribe((isLoggedIn: any) => {
      // console.log(`check via url for isUserLoggedIn ${isLoggedIn}`);
      // console.log(`checking for isUserLoggedIn ${isLoggedIn}`);
      if (isLoggedIn) {
        //  console.log(`checking for isUserLoggedIn ${isLoggedIn}`);
        this.setToastNotificationOnSubscriptionStatus();
      }
    }); */
  }

  visibleSidebar1(flag: boolean) {
    // console.log(`Flag ${flag}`);
    this.showSideBar = flag;
  }

  showSticky() {
    this.messageService.add({
      key: 'toast1',
      severity: 'info',
      summary: 'Sticky',
      detail: 'Message Content',
      sticky: true,
    });
  }

  showBillingDueToast() {
    this.messageService.add({
      key: 'billingDueToast',
      severity: 'info',
      summary: 'Billing Due',
      detail: 'Your billing is due. Please make a payment.',
      sticky: true,
    });
  }

  showExpiredToast() {
    this.messageService.add({
      key: 'expiredToast',
      severity: 'error',
      summary: 'Expired',
      detail: 'Your subscription has expired.',
      sticky: true,
    });
  }

  showOverDueToast() {
    this.messageService.add({
      key: 'overDueToast',
      severity: 'warn',
      summary: 'Over Due',
      detail: 'Payment overdue. Please pay immediately.',

      sticky: true,
    });
    //Payment overdue. Please pay immediately.
  }

  ngOnDestroy() {
    this.logger.debug(`AppComponent Destroy`);
    this.fontSizeSubscription.unsubscribe();
    this.sessionExpiredSubscription?.unsubscribe();
    this.forcedLogoutSubscription?.unsubscribe();
    this.accessDeniedSubscription?.unsubscribe();
    this.planLimitSubscription?.unsubscribe();
    this.foregroundPushSubscription?.unsubscribe();
    this.legalChangedSub?.unsubscribe();
    this.stopTicketBadgePolling();
    window.removeEventListener('resize', this.onWindowResize.bind(this));
    document.body.style.margin = ''; // Set this to the default margin you use in other pages
  }

  /** Called on every non-hidden route change — starts listening once (fetch once + live pushes), then
   *  is a no-op. The count refreshes only when the server says this company's tickets changed, or on
   *  the live connection's catch-up (tab visible again / reconnect). No timer. */
  private startTicketBadgePolling(): void {
    if (this.ticketLiveSub) return;
    if (!this.networkStatusService.isOnline) return;
    if (!hasWebSession()) return;   // Check Your Email, sign-up and similar pages are not hidden routes but nobody is signed in there
    this.fetchTicketCount();
    this.ticketLiveSocket.connect();
    const releaseTickets = this.ticketLiveSocket.declareInterest(['TICKETS']);
    const sub = this.ticketLiveSocket.changes$
      .pipe(filter(c => c.resync || c.sections.includes('TICKETS')), debounceTime(500))
      .subscribe(() => { if (this.networkStatusService.isOnline) this.fetchTicketCount(); });
    sub.add(() => this.ticketLiveSocket.disconnect());
    sub.add(releaseTickets);
    this.ticketLiveSub = sub;
  }

  private stopTicketBadgePolling(): void {
    this.ticketLiveSub?.unsubscribe();
    this.ticketLiveSub = null;
  }

  private fetchTicketCount(): void {
    if (!hasWebSession()) return;
    this.ticketService.getOpenCount().subscribe({
      next: r => {
        this.openTicketCount = r?.open ?? 0;
        this.buildSpeedDialItems();
      },
      error: () => { /* silent — badge just stays at 0 */ }
    });
  }

  // One-time-per-device push-permission priming, evaluated on the first authenticated route of a
  // session. Skips the onboarding routes (their own screens already show this exact dialog), any
  // device that has already been asked (ll_push_prompt_seen), and any state where the OS/browser
  // can no longer show its prompt anyway (granted / denied / unsupported). The short delay keeps
  // the dialog clear of first-paint and the splash-hide window-focus settle.
  private maybePromptForPushPermission(url: string): void {
    if (this.pushPromptEvaluated) return;
    if (url.startsWith('/account-setup') || url.startsWith('/email-verification') || url.startsWith('/registration')) {
      return;
    }
    this.pushPromptEvaluated = true;
    let seen = false;
    try { seen = localStorage.getItem('ll_push_prompt_seen') === '1'; } catch { /* private mode / storage off */ }
    if (seen) return;
    if (!this.pushNotificationService.isPermissionPromptable()) return;
    setTimeout(() => { this.showEnableNotificationsPrompt = true; }, 1800);
  }

  onEnableNotificationsPromptDone(): void {
    // The dialog component also writes this on either choice; set it here too so a dismissal that
    // somehow skips that path still counts as "asked".
    try { localStorage.setItem('ll_push_prompt_seen', '1'); } catch { /* private mode / storage off */ }
  }

  private loadBootstrapData(): void {
    // Redundant with SyncEngineService's own online-transition effect (constructor injection
    // above) — cheap no-op when nothing's queued, but catches the case where the device was
    // already online before that effect last fired (e.g. this session's own bootstrap).
    this.syncEngineService.trySync();

    // Registers a pending FCM token once we know a session exists — the 'registration' listener
    // (set up in the constructor) can fire before login on cold start, so the actual backend
    // call is deferred to here. Idempotent — cheap no-op once the current token is registered.
    this.pushNotificationService.registerPendingTokenIfNeeded();

    this.bootstrapService.load().subscribe({
      next: (res: BootstrapResponse) => {
        this.aiChatEnabled = res.aiChatStatus?.enabled ?? true;
        this.aiChatShared.enabled.set(this.aiChatEnabled); // the profile menu's "Ask Linc" row follows this
        this.buildSpeedDialItems();
        this.handleSubscriptionStatus(res.subscriptionStatus);
        if (!this.legalCheckDone) {
          this.legalCheckDone = true;
          // COOKIE is filtered out here — it now has its own real re-consent flow (the actual
          // Cookie Preferences banner/dialog) instead of the generic notice banner below, which
          // never actually re-collects consent for any document type, just links to "review in
          // your profile".
          this.handleOutdatedOrAutoAccept((res.legalOutdated || []).filter(d => d.documentType !== 'COOKIE'));
          this.resolveCookieConsentForAccount(res.legalOutdated || [], res.legalAcceptances || []);
        }
      },
      error: (error: any) => {
        this.logger.error(`Error while fetching bootstrap data ${error}`);
        // Most of the fanned-out services (beta/quality/timezone) are fine keeping their
        // last-known/default value here — but InventoryModuleService's default is `false`
        // (fail CLOSED), which is the wrong direction for a cold app start that's ALREADY
        // offline: bootstrap is the ONLY live call site that ever populates `visible`
        // (setFromBootstrap, above) — InventoryModuleService.load()/checkVisible() is never
        // invoked from anywhere else in the app — so on a cold offline start, `visible` gets
        // permanently stuck at `false` for the rest of the session with nothing to ever
        // correct it, hiding Flexible Layout entirely regardless of what's actually persisted.
        // load() has its own deliberate offline-safe fallback (catchError(() => of(true)),
        // matching PermissionGuard's convention) that bootstrap's plain tap() doesn't — call it
        // here as a fail-open recovery path specifically for this one flag. Confirmed live
        // 2026-08-05: Product column (Flexible Layout) silently missing offline after a cold
        // start, reappearing only once genuinely online refreshed bootstrap successfully.
        this.inventoryModuleService.load();
      },
    });
    // Refresh the offline-grace lease on every online protected-route visit — keeps the
    // 7-day offline window sliding forward while the device checks in normally. Fire-and-forget:
    // a failed refresh just means the existing (still valid) lease is used until the next success.
    this.offlineLeaseService.refresh().subscribe({
      error: (error: any) => this.logger.debug('Offline lease refresh failed (will retry next visit)', error),
    });
  }

  // Android: when the OS kills the app's process in the background (memory pressure /
  // OEM battery management — a rotation gesture can incidentally trigger this on some
  // devices), Capacitor always reloads the WebView at its fixed start URL ("/") on
  // restart, landing an already-logged-in user on the default dashboard instead of
  // wherever they were. Continuously remember the last "real" in-app route here so
  // LoginRedirectGuard can resume it — same mechanism as postLoginReturnUrl (deep-link
  // resume), kept as a separate key so the two can never clobber each other. Cleared
  // automatically on logout via LogoutService's localStorage.clear().
  private static readonly NON_RESUMABLE_ROUTE_PREFIXES = [
    'login', 'registration', 'account-setup', 'post-login', 'pagenotfound',
    'unauthorized', 'error', '/s/', 'public/', 'tally-sheet/share', 'tallysheet-view',
    'payment', 'confirm-account-setup', 'mobile-oauth-callback',
  ];

  private persistLastActiveRoute(currentUrl: string): void {
    const isResumable = !AppComponent.NON_RESUMABLE_ROUTE_PREFIXES.some((prefix) =>
      currentUrl.includes(prefix),
    );
    if (isResumable) {
      localStorage.setItem('lastActiveRoute', currentUrl);
    }
  }

  // localStorage[AppConstants.USER] is only written by the fresh interactive-login flow and is
  // never rehydrated on a resumed session backed by a still-valid HttpOnly auth cookie — same
  // gotcha already documented in HeaderComponent.ngAfterViewInit and
  // PushNotificationService.registerPendingTokenIfNeeded. The auth cookie itself, set by the
  // backend before the post-login redirect, is the only reliable "am I logged in" signal.
  private isLoggedInViaAuthCookie(): boolean {
    return document.cookie
      .split('; ')
      .some((c) => c.startsWith(AppConstants.ENCRYPTION_KEY_COOKIE_NAME + '='));
  }

  private buildSpeedDialItems(): void {
    const ticketBadge = this.openTicketCount > 0
      ? String(this.openTicketCount > 9 ? '9+' : this.openTicketCount)
      : undefined;
    const items: MenuItem[] = [];
    if (this.aiChatEnabled) {
      items.push({
        icon: 'pi pi-comment',
        tooltipOptions: { tooltipLabel: 'Linc AI Help', tooltipPosition: 'left' },
        command: () => this.aiChatWidget?.toggleChat()
      });
    }
    items.push({
      icon: 'pi pi-headphones',
      tooltipOptions: { tooltipLabel: 'Support Tickets', tooltipPosition: 'left' },
      badge: ticketBadge,
      command: () => this.router.navigate(['/support/tickets'])
    });
    this.speedDialItems = items;
  }

  private handleSubscriptionStatus(response: any | null) {
        if (!response || !response.success) {
          return;
        }

        const status = response.message as SubscriptionStatus;
        const daysRemaining = Number(response.daysRemaining);
        const graceDaysRemaining = Number(response.graceDaysRemaining);
        const trial = !!response.trial;
        const toastKey = `${status}-${trial}-${daysRemaining}-${graceDaysRemaining}`;
        if (this.lastSubscriptionToastKey === toastKey) {
          return;
        }
        this.lastSubscriptionToastKey = toastKey;

        // Apple forbids explicit purchase/renewal-direction copy in the iOS app (no in-app path
        // to act on it there either — see IosPurchaseBlockGuard) — swap to neutral wording.
        const isIos = isIosNative();

        if (status === SubscriptionStatus.UNDER_GRACE_PERIOD) {
          this.messageService.add({
            key: 'billingDueToast',
            severity: 'warn',
            summary: trial ? 'Trial grace period' : 'Subscription grace period',
            detail: isIos
              ? 'Your plan needs attention. Contact your account administrator.'
              : trial
                ? `Your trial has expired. You have ${graceDaysRemaining} grace day${graceDaysRemaining === 1 ? '' : 's'} left to choose a paid plan.`
                : `Your subscription has expired. You have ${graceDaysRemaining} grace day${graceDaysRemaining === 1 ? '' : 's'} left to renew.`,
            sticky: false,
          });
          return;
        }

        if (status === SubscriptionStatus.EXPIRED || status === SubscriptionStatus.TRIAL_EXPIRED || status === SubscriptionStatus.RENEWAL_LOCKED) {
          this.messageService.add({
            key: 'expiredToast',
            severity: 'error',
            summary: trial ? 'Trial expired' : 'Renewal required',
            detail: isIos
              ? 'Your plan needs attention. Contact your account administrator to continue.'
              : trial
                ? 'Your trial and grace period have ended. Please choose a paid plan to continue.'
                : 'Your subscription and grace period have ended. Please renew to continue.',
            sticky: true,
          });
          return;
        }

        if (status === SubscriptionStatus.UNDER_RENEWAL && daysRemaining >= 0) {
          this.messageService.add({
            key: 'billingDueToast',
            severity: 'warn',
            summary: trial ? 'Trial ending soon' : 'Subscription ending soon',
            detail: trial
              ? `Your trial period ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.`
              : `Your subscription expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.`,
            sticky: false,
          });
        }
  }

  public getRouterOutletState(outlet: RouterOutlet) {
    return outlet.isActivated ? outlet.activatedRoute : '';
  }

  prepareRoute(outlet: RouterOutlet) {
    return (
      outlet &&
      outlet.activatedRouteData &&
      outlet.activatedRouteData['animation']
    );
  }

  updateBodyPadding() {
    let padding: string;
    if (window.matchMedia('(min-width: 1200px)').matches) {
      padding = '1.5em'; // Extra Large
    } else if (window.matchMedia('(min-width: 992px)').matches) {
      padding = '1em';
    } else if (window.matchMedia('(min-width: 768px)').matches) {
      padding = '1em';
    } else if (window.matchMedia('(min-width: 576px)').matches) {
      padding = '0.75em';
    } else {
      padding = '0.5em';
    }
    if (window.location.href.includes('/login')) {
      this.showLogoSubject.next(false);

      padding = '0em';
    }
    document.body.style.padding = padding;
  }

  onConfigButtonClick() {
    this.appConfig.showConfigSidebar();
  }
  get visible(): boolean {
    return this.appConfig.state.configSidebarVisible;
  }
  set visible(_val: boolean) {
    this.appConfig.state.configSidebarVisible = _val;
  }
  sliderValue: number = 2; // Default to 'MD' (index 2 in the fontSizes array)


  fontSizes = [
    { label: 'XS', value: 0.75 },
    { label: 'SM', value: 0.875 },
    { label: 'MD', value: 1 },
    { label: 'LG', value: 1.125 },
    { label: 'XL', value: 1.25 },
    { label: '2XL', value: 1.5 },
  ];

  selectedFontSize: any = this.fontSizes[this.sliderValue].value;

  isDarkThemeEnabled = false;
  availableThemes = [
    { label: 'Light', value: Theme.Light },
    { label: 'Dark', value: Theme.Dark },
  ];
  selectedTheme = this.availableThemes[0].value;

  private updateFontSizeFromStorage(): void {
    const storedFontSize = localStorage.getItem(AppConstants.FONT_SIZE);

    if (storedFontSize) {
      const storedFontSizeNumber = parseFloat(storedFontSize);
      const index = this.fontSizes.findIndex(
        (item) => item.value === storedFontSizeNumber
      );
      if (index !== -1) {
        this.selectedFontSize = this.fontSizes[index];
      }
      // Always emit so the DOM is updated from stored value
      this.fontSizeService.changeFontSize(storedFontSizeNumber);
    } else {
      // Use device-aware default — no stored preference means auto-detect
      const deviceDefault = this.getDeviceDefaultFontSize();
      this.fontSizeService.changeFontSize(deviceDefault);
      const index = this.fontSizes.findIndex(item => item.value === deviceDefault);
      this.selectedFontSize = index !== -1 ? this.fontSizes[index] : this.fontSizes[2];
    }
  }

  private getDeviceDefaultFontSize(): number {
    if (this.utilService.isMobile()) return 0.875;   // XS → SM for readability
    if (this.utilService.isTab())   return 0.875;    // SM for tablets
    return 1;                                         // MD for desktop
  }

  onFontSizeChange(event: any) {
    this.logger.debug(`this.selectedFontSize ${JSON.stringify(event)}`);
    this.fontSizeService.changeFontSize(event);
    document.documentElement.style.fontSize = event + 'rem';
    this.cdr.detectChanges();
  }

  private onWindowResize(): void {
    // Auto-adjust only when user has not set a manual preference
    const stored = localStorage.getItem(AppConstants.FONT_SIZE);
    if (!stored) {
      const deviceDefault = this.getDeviceDefaultFontSize();
      const current = parseFloat(document.documentElement.style.fontSize || '1');
      if (Math.abs(current - deviceDefault) > 0.01) {
        this.fontSizeService.changeFontSize(deviceDefault);
      }
    }
  }

  updateTheme() {
    this.logger.debug(this.isDarkThemeEnabled); // Add this line
    this.selectedTheme = this.isDarkThemeEnabled ? Theme.Dark : Theme.Light;
    this.themeService.switchTheme(this.selectedTheme);
    this.cdr.detectChanges();
  }

  private updateThemeFromStorage(): void {
    const storedTheme = localStorage.getItem(AppConstants.THEME);
    if (storedTheme) {
      const index = this.availableThemes.findIndex(
        (item) => item.value === storedTheme
      );
      if (index !== -1) {
        this.selectedTheme = this.availableThemes[index].value;
      }
      this.isDarkThemeEnabled = this.selectedTheme === Theme.Dark;
    } else {
      this.selectedTheme = Theme.Light; // default value
      this.isDarkThemeEnabled = false;
    }
  }



  onSliderChange(event: any) {
    this.logger.debug(`onSliderChange ${JSON.stringify(event)}`)
    this.selectedFontSize = this.fontSizes[this.sliderValue].value;
    this.logger.debug(`this.selectedFontSize ${this.selectedFontSize}`)
    this.onFontSizeChange(this.selectedFontSize);
  }


  acceptSessionExpiredDialog() {
    this.logger.debug('AppComponent :: session expired dialog accepted');
    this.showSessionExpiredDialog = false;
    this.sessionExpiredAccept?.();
  }

  rejectSessionExpiredDialog() {
    this.logger.debug('AppComponent :: session expired dialog rejected');
    this.showSessionExpiredDialog = false;
    this.sessionExpiredReject?.();
  }

  acceptForcedLogoutDialog() {
    this.logger.debug('AppComponent :: forced logout dialog accepted');
    this.showForcedLogoutDialog = false;
    this.forcedLogoutInfo = null;
    // Same storage-clear as the session-expired dialog's own accept — tour-seen flags AND the "last user on this
    // device" markers survive; the rest (including the dead session's own cached data) does not. Dropping the markers
    // (as this used to) meant a DIFFERENT user logging in next was not recognised, so the previous user's offline
    // cache (picture, menu, lists) stayed and showed for them.
    resetOfflineIdentityVerified();
    clearLocalStorageKeepingDeviceIdentity();
    sessionStorage.clear();
    this.router.navigateByUrl('/login?sessionExpired=true').finally(() => this.forcedLogoutDialogService.reset());
  }

  /** Best-effort device-type icon from the free-text description the backend sends — same
   *  heuristic as ConfirmLogoutDevicesComponent.deviceIcon (that component's version isn't
   *  exported as a shared utility, and this is the only other call site). */
  forcedLogoutDeviceIcon(device: string | null | undefined): string {
    const text = (device ?? '').toLowerCase();
    if (/\b(ipad|tablet)\b/.test(text)) return 'pi pi-tablet';
    if (/\b(iphone|android|mobile|ios)\b/.test(text)) return 'pi pi-mobile';
    return 'pi pi-desktop';
  }

  closeAccessDeniedDialog() {
    this.showAccessDeniedDialog = false;
    this.accessDeniedDialogService.close();
  }

  closePlanLimitDialog() {
    this.showPlanLimitDialog = false;
    this.planLimitDialogService.close();
  }

  goToPlanLimitUpgrade() {
    this.closePlanLimitDialog();
    this.router.navigateByUrl('/subscriptions');
  }

  goToPlanLimitManage() {
    if (!this.planLimitManageRoute) return;
    const route = this.planLimitManageRoute;
    this.closePlanLimitDialog();
    this.router.navigateByUrl(route);
  }


  acceptAllCookies() {
    this.saveCookieConsent(true);
  }

  rejectNonEssentialCookies() {
    this.saveCookieConsent(false);
  }

  saveCookiePreferences() {
    this.saveCookieConsent(this.analyticsCookiesEnabled);
  }

  /** true when at least one outstanding document was accepted before (so "updated" is true); false = nothing accepted yet (a new account). */
  get legalHasUpdated(): boolean {
    return this.legalOutdatedDocs.some(d => !!d.yourVersion);
  }

  /** The tick on the sign-up page, remembered in this tab (sessionStorage) when a social sign-up left the page; fresh for 30 minutes. */
  private consumeSignupAgreement(): boolean {
    try {
      const v = sessionStorage.getItem(SIGNUP_AGREED_KEY);
      if (!v) return false;
      sessionStorage.removeItem(SIGNUP_AGREED_KEY);
      const age = Date.now() - Number(v);
      return age >= 0 && age < 30 * 60 * 1000;
    } catch {
      return false;
    }
  }

  /**
   * Right after a social sign-up the user has accepted nothing yet (the agreement was ticked on the sign-up page, but the
   * page was left for the provider). If that tick is fresh in this tab and EVERY outstanding document is a first-time one,
   * record them with the user's earlier agreement instead of showing "Updated legal documents". Anything else (a document
   * that really changed, or an older account) still shows the banner. If recording fails the banner shows as before.
   */
  private handleOutdatedOrAutoAccept(outdated: OutdatedDoc[]): void {
    const agreedAtSignup = this.consumeSignupAgreement();
    if (agreedAtSignup && outdated.length > 0 && outdated.every(d => !d.yourVersion)) {
      const items = outdated.map(d => ({ documentType: d.documentType, version: d.latestVersion }));
      this.legalService.recordBatchAcceptance(items).subscribe({
        next: () => { /* recorded: nothing to show */ },
        error: () => this.handleOutdatedLegalDocs(outdated),
      });
      return;
    }
    this.handleOutdatedLegalDocs(outdated);
  }

  /** Re-read what is outstanding after an acceptance was recorded somewhere; hide the banner and dialog when nothing is left. */
  private refreshOutdatedLegalDocs(): void {
    this.legalService.getOutdated().subscribe((list) => {
      const docs = (list || []).filter(d => d.documentType !== 'COOKIE');
      if (docs.length === 0) {
        this.legalOutdatedDocs = [];
        this.showLegalUpdateBanner = false;
        this.showLegalAcceptDialog = false;
      } else if (this.legalOutdatedDocs.length > 0) {
        this.legalOutdatedDocs = docs;
      }
    });
  }

  private handleOutdatedLegalDocs(outdated: OutdatedDoc[]): void {
    if (!outdated || outdated.length === 0) return;
    const cacheKey = outdated.map(d => `${d.documentType}:${d.latestVersion}`).sort().join('|');
    const dismissed = localStorage.getItem(this.legalBannerDismissKey);
    if (dismissed === cacheKey) return;
    this.legalOutdatedDocs = outdated;
    this.showLegalUpdateBanner = true;
  }

  // NOTE: this only hides the banner in THIS browser, and only until logout — LogoutService
  // (and login) call window.localStorage.clear(), which wipes the key below. That is why the
  // banner kept reappearing on every login. acceptAllLegalDocs() is the permanent answer.
  dismissLegalBanner(): void {
    const cacheKey = this.legalOutdatedDocs.map(d => `${d.documentType}:${d.latestVersion}`).sort().join('|');
    localStorage.setItem(this.legalBannerDismissKey, cacheKey);
    this.showLegalUpdateBanner = false;
  }

  showLegalAcceptDialog = false;
  legalAcceptChecked = false;
  savingLegalAcceptance = false;

  openLegalAcceptance(): void {
    this.legalAcceptChecked = false;
    this.showLegalAcceptDialog = true;
  }

  /**
   * Records acceptance for every outstanding document in ONE call. Sends each document's real
   * latestVersion (NOT a hardcoded '1.0' like subscription-management's checkout call does) —
   * with the wrong version the server would still consider the document outdated and the banner
   * would come straight back.
   */
  acceptAllLegalDocs(): void {
    if (!this.legalAcceptChecked || this.savingLegalAcceptance) { return; }
    const items = this.legalOutdatedDocs.map(d => ({
      documentType: d.documentType,
      version: d.latestVersion,
    }));
    if (!items.length) { this.showLegalAcceptDialog = false; return; }

    this.savingLegalAcceptance = true;
    this.legalService.recordBatchAcceptance(items).subscribe({
      next: () => {
        this.savingLegalAcceptance = false;
        this.showLegalAcceptDialog = false;
        this.showLegalUpdateBanner = false;
        this.legalOutdatedDocs = [];
        this.messageService.add({
          severity: 'success',
          summary: 'Thank you',
          detail: 'Your acceptance has been recorded.',
          life: 4000,
        });
      },
      error: () => {
        // Keep the banner and dialog state honest — do not pretend it saved.
        this.savingLegalAcceptance = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Could not save',
          detail: 'We could not record your acceptance. Please try again.',
          life: 6000,
        });
      },
    });
  }

  getDocRoute(documentType: string): string {
    const routes: Record<string, string> = { COOKIE: 'cookies' };
    return routes[documentType] ?? documentType.toLowerCase();
  }

  private initCookieConsent(): void {
    // Apple 5.1.2(i) fix (2026-09-01): iOS never shows this banner at all — Essential/Functional
    // cookies are "Always On"/non-negotiable already, and Analytics never loads on iOS (see
    // loadAnalytics()), so there is nothing left on iOS for this banner to ask consent for.
    // Showing it anyway is what got the app flagged as a custom tracking-permission prompt.
    if (isIosNative()) { return; }
    const stored = localStorage.getItem(this.cookieConsentKey);
    if (stored) {
      try {
        const consent = JSON.parse(stored);
        this.analyticsCookiesEnabled = consent.analytics === true;
        this.updateAnalyticsConsent(this.analyticsCookiesEnabled);
        if (this.analyticsCookiesEnabled) {
          this.loadAnalytics();
        }
      } catch (error) {
        this.showCookieConsent = true;
      }
      return;
    }
    // Real fix 2026-08-27: a logged-in user with no local choice yet (new browser/device,
    // cleared storage) no longer falls through to a geo-default banner here — whether they
    // actually need one depends on their SERVER-side record, which isn't known yet at this
    // point in the app's startup (bootstrap hasn't resolved). resolveCookieConsentForAccount()
    // (called once bootstrap resolves, see loadBootstrapData) decides for them instead — either
    // silently hydrating from their existing server record (no banner, any device) or falling
    // through to this same geo-default lookup itself if they truly have no record anywhere yet.
    // Skipping this avoids a duplicate geo lookup and avoids flashing the banner before the
    // server's answer is known. Anonymous visitors (marketing site, login page) are unaffected —
    // they have no account for a server record to exist against, so they always take this path.
    if (this.isLoggedInViaAuthCookie()) {
      return;
    }
    // First-time visitor: ask geo service which analytics default applies for this IP region.
    // GDPR/UK-GDPR/DPDP countries → analyticsDefault=false (must opt-in).
    // All other regions → analyticsDefault=true (opt-out model).
    // 3-second timeout and any error both fall back to false (safest legal default).
    this.http.get<{ analyticsDefault: boolean }>(`${AppConstants.API_URL}api/v1/geo/consent`)
      .pipe(
        timeout(3000),
        catchError(() => of({ analyticsDefault: false }))
      )
      .subscribe(res => {
        this.analyticsCookiesEnabled = res.analyticsDefault;
        this.showCookieConsent = true;
      });
  }

  // Same geo-default lookup used above for anonymous visitors, reused by
  // resolveCookieConsentForAccount() for a logged-in user with no local AND no server signal at
  // all (a genuinely first-ever consent decision) — kept as its own method so both callers share
  // one implementation instead of drifting into two copies.
  private showCookieBannerWithGeoDefault(): void {
    this.http.get<{ analyticsDefault: boolean }>(`${AppConstants.API_URL}api/v1/geo/consent`)
      .pipe(
        timeout(3000),
        catchError(() => of({ analyticsDefault: false }))
      )
      .subscribe(res => {
        this.analyticsCookiesEnabled = res.analyticsDefault;
        this.showCookieConsent = true;
      });
  }

  // Real fix 2026-08-27: decides what a LOGGED-IN user should see for Cookie Consent, using
  // their server-side legal_acceptance record instead of only this browser's localStorage —
  // this is what makes consent follow the account across devices instead of re-asking on every
  // new browser (the original complaint). Called once per session, right alongside
  // handleOutdatedLegalDocs, from loadBootstrapData()'s existing one-shot legalCheckDone guard.
  private resolveCookieConsentForAccount(outdated: OutdatedDoc[], acceptances: LegalAcceptanceStatus[]): void {
    // Apple 5.1.2(i) fix (2026-09-01): same iOS skip as initCookieConsent() — this is the OTHER
    // independent entry point that can set showCookieConsent = true (for a logged-in user whose
    // server-side cookie consent is outdated/missing), so it needs the same gate.
    if (isIosNative()) { return; }
    const cookieOutdated = outdated.find(d => d.documentType === 'COOKIE');
    const cookieStatus = acceptances?.find(a => a.documentType === 'COOKIE');
    if (cookieStatus?.latestVersion) {
      this.cookieConsentVersion = cookieStatus.latestVersion;
    }

    if (!cookieOutdated) {
      // Server record is current — this is the actual fix: never show the banner on this (or
      // any) device again until the version changes. Server is authoritative, not localStorage.
      const analytics = cookieStatus?.preferences?.analytics === true;
      this.analyticsCookiesEnabled = analytics;
      this.updateAnalyticsConsent(analytics);
      if (analytics) {
        this.loadAnalytics();
      }
      this.showCookieConsent = false;
      // Write-through so a quick reload on this same browser doesn't need another server round
      // trip to know the user already consented.
      localStorage.setItem(this.cookieConsentKey, JSON.stringify({
        essential: true,
        analytics,
        acceptedAt: cookieStatus?.acceptedAt ?? new Date().toISOString()
      }));
      return;
    }

    this.cookieConsentVersion = cookieOutdated.latestVersion;

    const storedRaw = localStorage.getItem(this.cookieConsentKey);
    if (cookieOutdated.yourVersion === null && storedRaw) {
      // Never accepted under this account, but this device already has a choice from before
      // login (or from an earlier anonymous visit) — migrate it silently, once, no re-prompt.
      try {
        const stored = JSON.parse(storedRaw);
        const analytics = stored.analytics === true;
        this.legalService.recordCookieAcceptance(this.cookieConsentVersion, analytics).subscribe({
          error: (e) => this.logger.debug('Silent cookie-consent migration failed', e),
        });
      } catch {
        // Malformed local value — fall through to a real prompt below instead of guessing.
        this.promptCookieConsentReconfirm(cookieStatus);
      }
      return;
    }

    // Either a genuinely first-ever decision (no local value, no server record) or the policy
    // version changed since they last consented — both need a real, fresh answer.
    this.promptCookieConsentReconfirm(cookieStatus);
  }

  // Shows the REAL Cookie Preferences banner/dialog (not the generic informational Legal Update
  // Banner, which doesn't actually re-collect consent for any document type) — pre-seeded with
  // whatever prior signal exists (stale server choice, else local storage, else a fresh
  // geo-based default), so re-confirming after a version bump isn't a totally blank decision.
  private promptCookieConsentReconfirm(cookieStatus: LegalAcceptanceStatus | undefined): void {
    if (cookieStatus?.preferences && typeof cookieStatus.preferences.analytics === 'boolean') {
      this.analyticsCookiesEnabled = cookieStatus.preferences.analytics;
      this.showCookieConsent = true;
      return;
    }
    const storedRaw = localStorage.getItem(this.cookieConsentKey);
    if (storedRaw) {
      try {
        this.analyticsCookiesEnabled = JSON.parse(storedRaw).analytics === true;
        this.showCookieConsent = true;
        return;
      } catch {
        // fall through to geo default below
      }
    }
    this.showCookieBannerWithGeoDefault();
  }

  private saveCookieConsent(analytics: boolean) {
    this.analyticsCookiesEnabled = analytics;
    localStorage.setItem(this.cookieConsentKey, JSON.stringify({
      essential: true,
      analytics,
      acceptedAt: new Date().toISOString()
    }));
    this.showCookieConsent = false;
    this.showCookiePreferences = false;
    this.updateAnalyticsConsent(analytics);
    if (analytics) {
      this.loadAnalytics();
    }
    // Real fix 2026-08-27: also persist to the account when logged in — covers both a genuine
    // first save and a version-bump re-prompt save through this one existing path. Anonymous
    // visitors are unaffected (isLoggedInViaAuthCookie() is false pre-login, matching decision
    // 1.2 — localStorage only, no server call).
    if (this.isLoggedInViaAuthCookie()) {
      this.legalService.recordCookieAcceptance(this.cookieConsentVersion, analytics).subscribe({
        error: (e) => this.logger.debug('Cookie acceptance sync failed', e),
      });
    }
  }

  private updateAnalyticsConsent(analytics: boolean) {
    const gtag = (window as any).gtag;
    if (typeof gtag !== "function") {
      return;
    }
    gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied"
    });
  }

  // The "get the app" / "open in app" banner moved to its own standalone component
  // (OpenInAppBannerComponent), rendered once in app.component.html. It owns all of the platform
  // detection, 30-day dismiss memory and androidHandoff / MOBILE_APP_BANNER_ENABLED guards that
  // used to live here, and adds route-aware "Open in app" behaviour on share links.

  private loadAnalytics() {
    // Apple 5.1.2(i) fix (2026-09-01): belt-and-suspenders — initCookieConsent()/
    // resolveCookieConsentForAccount() already never call this on iOS, but this guard makes sure
    // GA4 can never load on iOS even if some future code path calls loadAnalytics() directly.
    if (isIosNative()) { return; }
    // Android's Capacitor webview also reports app.lumberlinq.com (capacitor.config.ts hostname),
    // so this still counts Android; it blocks the old app-vps.rikexim.com host and local builds.
    if (!this.analyticsId || location.hostname !== 'app.lumberlinq.com') {
      return;
    }
    const w = window as any;
    if (typeof w.gtag !== 'function') {
      w.dataLayer = w.dataLayer || [];
      // gtag.js only reads consent/config commands pushed as an `arguments` object, not an array.
      w.gtag = function () { w.dataLayer.push(arguments); };
      w.gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      });
    }
    this.updateAnalyticsConsent(true);
    if (document.getElementById(this.analyticsScriptId)) {
      (window as any).gtag("config", this.analyticsId, { anonymize_ip: true });
      return;
    }
    const script = document.createElement("script");
    script.id = this.analyticsScriptId;
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=" + this.analyticsId;
    script.onload = () => {
      (window as any).gtag("js", new Date());
      (window as any).gtag("config", this.analyticsId, { anonymize_ip: true });
    };
    document.head.appendChild(script);
  }


  /* @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // metaKey is for Mac, ctrlKey is for Windows
    if (event.metaKey && event.shiftKey && event.key.toLowerCase() === 'o') { 
      // this.isMenuExpand = true;
      this.sideBarMenuSharedService.isMenuExpanded.emit(true);
      setTimeout(() => {
        // this.searchInput.nativeElement.focus();  
        this.sidebarComponent.focusSearchInput();

      }, 500);
      

    }
  } */
}
